import { useState, useEffect } from 'preact/hooks'
import { apiClient, handoffUrl, InitData, navigerUd, tenantSiteUrl, udLink } from '../../core'
import { t, formatMoney, formatMoneyParts } from '../../core/i18n'
import { PurchaseModal } from '../../components/PurchaseModal'
import { ContactFormWidget } from '../contact-form/ContactFormWidget'
import { planCta, showsPrice } from './plan-cta'
import { grupperPlaner, maanederFor, periodeNoegle, valgtPlan, type PeriodeNoegle, type PlanKort } from '../../core/plan-groups'

interface PricingPlan {
  id: string
  name: string
  description?: string | null
  price: number
  period: string | null
  periodSuffix?: string | null
  features: string[]
  isPopular?: boolean
  isComingSoon?: boolean
  membershipCategory?: string | null
  /** Planlængden (validity_days): afregningsperioden i købs-vinduets ordreoversigt. */
  validityDays?: number | null
  /** Planens startdato (ISO). Mangler den (ældre server), regnes planen for startende straks. */
  activatesFrom?: string | null
  /** Planens «Vis Se hold-knap». Mangler den (ældre server), vises knappen. */
  showClassesCta?: boolean
  /** Studiets egen tekst til «Kontakt mig»-knappen (kun sat på kontakt-planer). */
  contactButtonText?: string | null
  /** OPG-0130: planer i samme gruppe vises som ét kort med periodevælger. */
  planGroup?: string | null
  /** Alle deltagere på planen er momsfri (studiets fritagelse er virksom): viser «Fra» og «Momsfri pris». */
  vatFreeAll?: boolean
}

interface Props {
  initData: InitData
  // Restrict + order plans by id. null = show all.
  planIds?: string[] | null
  showFeatures?: boolean
  title?: string | null
  // Visual layout: "cards" (default), "table", or "compact"
  layout?: 'cards' | 'table' | 'compact'
  // Show the All / <category> chips above the plans, like the studio's own
  // pricing page. Ignored when there is only one category to filter on.
  categoryFilter?: boolean
  // Where the "See classes" CTA points. Default is the studio's own booking
  // page; an embedded site can send visitors to its OWN schedule section
  // instead (e.g. "#schedule"), so browsing does not leave their site.
  classesHref?: string | null
  // Own label on the "See classes" button from the embed attribute button-text
  // (OPG-0064). Wins over the studio's setting under Admin → Widgets.
  buttonText?: string | null
}

/**
 * PricingWidget — mirrors HS-native PricingPageContent styling.
 *
 * Card layout (per HS-native studio template):
 *   - Plan name (bold, 1.5rem)
 *   - Big price number (3.5rem, bold) + small "kr" suffix
 *   - Period below ("pr. md", "pr. gang")
 *   - Description (gray, 0.95rem)
 *   - Features with orange ✓
 *   - Outlined "Se hold" CTA at bottom
 *   - Popular: 2px orange border + gradient pill above
 */
export function PricingWidget({ initData, planIds = null, showFeatures = true, title = null, layout = 'cards', categoryFilter = false, classesHref = null, buttonText = null }: Props) {
  const str = t(initData.locale)
  // Studiets egen tekst på «Se hold»-knappen (OPG-0064): embed-attributten button-text
  // vinder over indstillingen på /admin/widgets, ellers standarden på studiets sprog.
  // Samme loft (30 tegn) som indstillingen, så en lang tekst ikke sprænger knappen.
  const attrLabel = buttonText?.replace(/\s+/g, ' ').trim().slice(0, 30) || null
  const seeClassesLabel = attrLabel || initData.pricing?.buttonText || str.seeClasses
  // `title` fra embed-koden vinder; ellers studiets egen prisside-overskrift (V516); ellers ingen titel (som før).
  const heading = title || initData.pricing?.heading || null
  // The pricing API returns membershipCategory as a raw system value
  // ('klippekort'/'subscription') for auto-grouped plans; localise those two
  // known values via the widget strings so an English (or German, ...) studio
  // doesn't see the Danish/enum heading. Custom, tenant-authored categories are
  // shown exactly as entered.
  const categoryLabel = (cat: string): string => {
    const key = cat.trim().toLowerCase()
    if (key === 'klippekort') return str.klippekort
    if (key === 'subscription') return str.subscriptionTitle
    // 'single' is the system value for drop-in plans. Without this it leaked the
    // raw enum into the heading and the category chips.
    if (key === 'single') return str.dropIn
    // 'contact' = «Kontakt mig»-planer; native viser dem under fanen «Andet».
    if (key === 'contact') return str.categoryOther
    return cat
  }
  const [plans, setPlans] = useState<PricingPlan[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // null = the "All" chip. Holds the raw category value, not the localised label.
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  // Sat når kunden trykker «Køb» på et KLIPPEKORT: åbner inline-købs-modalen på
  // studiets egen side (abonnement bruger stadig handoffet — se ctaClick nedenfor).
  const [purchasePlan, setPurchasePlan] = useState<PricingPlan | null>(null)
  // Sat når kunden trykker «Kontakt mig» på en kontakt-plan: åbner kontaktformularen
  // i et vindue, ligesom ContactModal på studiets egen prisside (OPG-0063).
  const [contactPlan, setContactPlan] = useState<PricingPlan | null>(null)
  // OPG-0130: valgt periode pr. gruppekort (kortets nøgle → plan-id), som på studiets prisside.
  const [valgtIGruppe, setValgtIGruppe] = useState<Record<string, string>>({})

  useEffect(() => {
    async function fetch() {
      const result = await apiClient.get<PricingPlan[]>('/api/widget/pricing')
      if (result.data) setPlans(result.data)
      if (result.error) setError(result.error)
      setLoading(false)
    }
    fetch()
  }, [])

  if (loading) {
    // Skeleton loading — 3 placeholder cards
    return (
      <div>
        <style>{pricingStyles}</style>
        <style>{skeletonStyles}</style>
        <div class="hsw-pricing-grid">
          {[0, 1, 2].map(i => (
            <div key={i} class="hsw-skeleton-card">
              <div class="hsw-skeleton hsw-sk-name" />
              <div class="hsw-skeleton hsw-sk-price" />
              <div class="hsw-skeleton hsw-sk-period" />
              <div class="hsw-skeleton hsw-sk-line" />
              <div class="hsw-skeleton hsw-sk-line" />
              <div class="hsw-skeleton hsw-sk-line" style={{ width: '70%' }} />
              <div class="hsw-skeleton hsw-sk-button" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <style>{pricingStyles}</style>
        <div role="alert" style={{ padding: '1rem 1.5rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 'var(--yoga-radius, 0)', color: '#b91c1c', maxWidth: '600px', margin: '0 auto' }}>
          <strong>⚠️ {error}</strong>
        </div>
      </div>
    )
  }

  // Filter + order by planIds attribute (if provided)
  const visiblePlans = planIds && planIds.length > 0
    ? planIds
        .map(id => plans.find(p => p.id === id))
        .filter((p): p is PricingPlan => Boolean(p))
    : plans

  if (!visiblePlans.length) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#737373' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>💰</div>
        <p>{str.noPricingPlans}</p>
      </div>
    )
  }

  // CTA targets — embedded widgets run on third-party sites, so purchases
  // redirect to the tenant subdomain where auth + payment integration lives.
  const tenantBaseUrl = tenantSiteUrl(initData)
  // Only the "See classes" CTA is redirectable: a purchase still has to run on
  // the studio origin (session + payment integration), so purchaseHref is not.
  // ⛔ `/classes`, IKKE `/booking`: `/booking` er reserveret til booking-only-mode
  // (kun 4 af 90 tenants), og BookingPageClient sender alle andre videre til `/`.
  // Fallbacken ville altså lande kunden på studiets forside i stedet for holdlisten
  // — stadig en blindgyde, bare en pænere. Målt under /ship 30-09-2026.
  const bookingHref = classesHref || handoffUrl(tenantBaseUrl, '/classes')
  // `autoStart=1` lands the visitor straight in the plan's payment step on the
  // tenant site (PricingPageContent auto-opens the purchase flow when logged
  // in, else routes through login with a returnUrl) — the tightest handoff we
  // can give the pricing widget, since membership checkout is session- and
  // same-origin-bound and can't run cross-origin inline.
  const purchaseHref = (planId: string) =>
    handoffUrl(tenantBaseUrl, `/pricing?plan=${planId}&autoStart=1`)
  const isPurchasable = (plan: PricingPlan) => {
    const cat = plan.membershipCategory?.toLowerCase()
    return cat === 'klippekort' || cat === 'subscription'
  }
  // Klippekort OG abonnement kan forsøges inline. Kernen afgør selv om netop dette
  // køb er simpelt nok (nyt køb, ingen op/nedgradering, ingen fast plads, intet
  // intro-tilbud) og afviser ellers med en kode — så falder modalen tilbage til
  // handoffet. Vi gætter altså ikke her; vi lader serveren svare.
  //
  // Abonnement kom med 01-10-2026 for Halgodt, der kun har MobilePay: et
  // MobilePay-abonnement har ingen kortfelter, så «inline» er reelt et hop til
  // MobilePay-appen og hjem igen til studiets egen side.
  const kanKoebesInline = (plan: PricingPlan) => {
    const cat = plan.membershipCategory?.toLowerCase()
    return cat === 'klippekort' || cat === 'subscription'
  }

  /**
   * «Se holdene» må ALDRIG ende blindt.
   *
   * `classes-href` er typisk et anker på studiets egen side. Peger det på et id
   * der ikke findes, gør browseren INTET ved et klik, og kunden tror widgeten
   * er død. Det skete for playgroundlx 29-09-2026: `classes-href="#classes"`
   * på en side uden `id="classes"`.
   *
   * Rækkefølgen er: det anker hun har peget på → skema- eller holdtype-widgeten
   * på samme side → studiets egen bookingside. Sidste udfald virker altid.
   */
  const scrollTilHold = (e: Event) => {
    if (!bookingHref.startsWith('#')) return
    e.preventDefault()
    const id = bookingHref.slice(1)
    const maal =
      (id ? document.getElementById(id) : null) ||
      document.querySelector('holdstyring-schedule') ||
      document.querySelector('holdstyring-weekly-schedule') ||
      document.querySelector('holdstyring-class-types')
    if (maal) {
      maal.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }
    // Samme grund som ved `bookingHref`: `/booking` redirecter til forsiden for
    // alle andre end booking-only-tenants, så sidste udvej skal være `/classes`.
    navigerUd(handoffUrl(tenantBaseUrl, '/classes'))
  }

  const ctaClick = (plan: PricingPlan) => {
    if (plan.isComingSoon) return (e: Event) => e.preventDefault()
    if (!isPurchasable(plan)) return scrollTilHold
    // Klippekort: åbn inline-købs-modalen i stedet for at hoppe til studiets side.
    // href'en (purchaseHref) bevares som progressiv fallback hvis JS er slået fra.
    if (kanKoebesInline(plan)) {
      return (e: Event) => { e.preventDefault(); setPurchasePlan(plan) }
    }
    return undefined
  }

  // «Kontakt mig»-plan: samme knaptekst som studiets egen prisside. Er
  // kontaktforespørgsler slået fra, ville /api/widget/contact afvise formularen,
  // så knappen sender i stedet kunden til studiets kontaktside.
  const kontaktHref = handoffUrl(tenantBaseUrl, '/contact')
  const kontaktInline = initData.features?.contactRequestsEnabled !== false
  const contactLabel = (plan: PricingPlan) => plan.contactButtonText?.trim() || str.contactMe

  /**
   * Planens knap, fælles for alle tre layouts. `fuld` = kortets lange tekst
   * («Køb <plan>», «Kommer snart»); tabel og kompakt bruger de korte.
   * null = ingen knap («Vis Se hold-knap» slået fra), præcis som native.
   */
  const renderCta = (plan: PricingPlan, cls: string | undefined, fuld: boolean) => {
    const cta = planCta(plan)
    if (cta === 'none') return null
    if (cta === 'contact') {
      return (
        <a
          href={kontaktHref}
          class={cls}
          onClick={kontaktInline ? (e: Event) => { e.preventDefault(); setContactPlan(plan) } : undefined}
          {...(kontaktInline ? {} : udLink(kontaktHref))}
        >
          {contactLabel(plan)}
        </a>
      )
    }
    const koeb = cta === 'purchase'
    const href = plan.isComingSoon ? '#' : (koeb ? purchaseHref(plan.id) : bookingHref)
    const label = plan.isComingSoon
      ? (fuld ? str.comingSoon : str.comingSoonShort)
      : koeb ? (fuld ? `${str.buy} ${plan.name}` : str.buy) : seeClassesLabel
    return (
      <a
        href={href}
        class={cls}
        aria-disabled={plan.isComingSoon}
        onClick={ctaClick(plan)}
        {...(koeb ? { target: '_blank', rel: 'noopener' } : udLink(href))}
      >
        {label}
      </a>
    )
  }

  // Group plans by membershipCategory, preserving first-occurrence order.
  // If all plans have null/empty category, render flat (single group with null key).
  const hasCategories = visiblePlans.some(p => p.membershipCategory && p.membershipCategory.trim() !== '')
  const groups: { category: string | null; plans: PricingPlan[] }[] = []
  if (hasCategories) {
    const indexByCategory = new Map<string, number>()
    for (const plan of visiblePlans) {
      const cat = plan.membershipCategory && plan.membershipCategory.trim() !== '' ? plan.membershipCategory : null
      const key = cat ?? '__uncategorized__'
      let idx = indexByCategory.get(key)
      if (idx === undefined) {
        idx = groups.length
        indexByCategory.set(key, idx)
        groups.push({ category: cat, plans: [] })
      }
      groups[idx].plans.push(plan)
    }
  } else {
    groups.push({ category: null, plans: visiblePlans })
  }

  // Category chips mirror the All / Drop-in / Punch card switch on the studio's
  // own pricing page. Only worth showing when there is something to switch
  // between, so a studio with a single category never sees a pointless "All".
  const filterableCategories = groups
    .map(g => g.category)
    .filter((c): c is string => Boolean(c))
  const showFilter = categoryFilter && filterableCategories.length > 1
  const visibleGroups = showFilter && activeCategory
    ? groups.filter(g => g.category === activeCategory)
    : groups

  const filterBar = showFilter ? (
    <div class="hsw-pricing-filter" role="group">
      <button
        type="button"
        class={`hsw-pricing-chip${activeCategory === null ? ' hsw-chip-active' : ''}`}
        aria-pressed={activeCategory === null}
        onClick={() => setActiveCategory(null)}
      >
        {str.filterAll}
      </button>
      {filterableCategories.map(cat => (
        <button
          key={cat}
          type="button"
          class={`hsw-pricing-chip${activeCategory === cat ? ' hsw-chip-active' : ''}`}
          aria-pressed={activeCategory === cat}
          onClick={() => setActiveCategory(cat)}
        >
          {categoryLabel(cat)}
        </button>
      ))}
    </div>
  ) : null

  // Big-number layout keeps amount and currency marker as separate spans;
  // symbolFirst decides which side the marker goes ("£150" vs "150 kr").
  const money = (amount: number) => formatMoneyParts(amount, initData.locale, initData.currency)

  // OPG-0130: kortets valgte plan, overskrift og periodevælger. Hvert valg er en hel plan,
  // så pris, knap og køb følger den valgte plan uændret.
  const PERIODE_TEKST: Record<PeriodeNoegle, string> = {
    fourWeeks: str.planPeriodFourWeeks,
    oneMonth: str.planPeriodOneMonth,
    threeMonths: str.planPeriodThreeMonths,
    sixMonths: str.planPeriodSixMonths,
    twelveMonths: str.planPeriodTwelveMonths,
  }
  const kortPlan = (kort: PlanKort<PricingPlan>) => valgtPlan(kort, valgtIGruppe[kort.noegle])
  const kortTitel = (kort: PlanKort<PricingPlan>) => kort.gruppe ?? kortPlan(kort).name
  const periodeVaelger = (kort: PlanKort<PricingPlan>) => {
    if (!kort.gruppe) return null
    const valgt = kortPlan(kort)
    return (
      <div class="hsw-period-picker" role="radiogroup" aria-label={str.planPeriodPicker}>
        {kort.planer.map(p => {
          const noegle = kort.periodeEtiketter ? periodeNoegle(p.validityDays) : null
          const aktiv = p.id === valgt.id
          return (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={aktiv}
              class={`hsw-period-option${aktiv ? ' hsw-period-active' : ''}`}
              onClick={() => setValgtIGruppe(f => ({ ...f, [kort.noegle]: p.id }))}
            >
              {noegle ? PERIODE_TEKST[noegle] : p.name}
            </button>
          )
        })}
      </div>
    )
  }

  const renderCard = (kort: PlanKort<PricingPlan>) => {
    const plan = kortPlan(kort)
    const m = money(plan.price)
    const maaneder = kort.gruppe ? maanederFor(plan.validityDays) : null
    return (
    <div key={kort.noegle} class={`hsw-price-card${plan.isPopular ? ' hsw-popular' : ''}${plan.isComingSoon ? ' hsw-coming-soon' : ''}`}>
      {plan.isPopular && !plan.isComingSoon && (
        <span class="hsw-badge-popular">{str.popular}</span>
      )}
      {plan.isComingSoon && (
        <span class="hsw-badge-soon">{str.comingSoon}</span>
      )}
      <h3 class="hsw-price-name">{kortTitel(kort)}</h3>
      {periodeVaelger(kort)}

      {showsPrice(plan) && (
        <>
          <div class="hsw-price-amount-row">
            {plan.vatFreeAll && <span class="hsw-price-suffix" data-plan-price-from>{str.priceFromPrefix}</span>}
            {m.symbolFirst && <span class="hsw-price-suffix">{m.symbol}</span>}
            <span class="hsw-price-amount">{m.amount}</span>
            {!m.symbolFirst && <span class="hsw-price-suffix">{m.symbol}</span>}
          </div>
          {plan.vatFreeAll && (
            <div class="hsw-price-vatfree" data-plan-vat-free>{str.vatFreePrice}</div>
          )}
          {plan.period && plan.period !== 'kr' && (
            <div class="hsw-price-period">{plan.period}</div>
          )}
          {plan.periodSuffix && (
            <div class="hsw-price-period">{plan.periodSuffix}</div>
          )}
          {maaneder !== null && maaneder > 1 && (
            <div class="hsw-price-permonth" data-plan-per-month>
              {str.planPerMonth.replace('{amount}', formatMoney(Math.round((plan.price / maaneder) * 100) / 100, initData.locale, initData.currency))}
            </div>
          )}
        </>
      )}

      {plan.description && (
        <div class="hsw-price-desc">{plan.description}</div>
      )}

      {showFeatures && plan.features.length > 0 && (
        <ul class="hsw-price-features">
          {plan.features.map((f, i) => (
            <li key={i}><span class="hsw-check">✓</span>{f}</li>
          ))}
        </ul>
      )}

      {renderCta(plan, 'hsw-price-cta', true)}
    </div>
    )
  }

  // Inline-købs-modalen (klippekort). Delt på tværs af alle tre layouts, så et køb
  // ser ens ud uanset visning. purchaseHref er fallback-handoffet modalen bruger hvis
  // kernen afviser inline-købet.
  const purchaseModal = purchasePlan ? (
    <PurchaseModal
      plan={{
        id: purchasePlan.id,
        name: purchasePlan.name,
        price: purchasePlan.price,
        membershipCategory: purchasePlan.membershipCategory ?? null,
        validityDays: purchasePlan.validityDays ?? null,
        activatesFrom: purchasePlan.activatesFrom ?? null,
      }}
      initData={initData}
      purchaseHref={purchaseHref(purchasePlan.id)}
      onClose={() => setPurchasePlan(null)}
    />
  ) : null

  // «Kontakt mig»-vinduet: den samme formular som kontakt-widget'en, med planens
  // navn som overskrift. planId lader serveren sætte planen som emne.
  const contactModal = contactPlan ? (
    <div class="hsw-cp-backdrop" onClick={() => setContactPlan(null)}>
      <div
        class="hsw-cp-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={contactPlan.name}
        onClick={(e: Event) => e.stopPropagation()}
      >
        <button type="button" class="hsw-cp-close" onClick={() => setContactPlan(null)} aria-label={str.close}>×</button>
        <ContactFormWidget
          initData={initData}
          title={contactPlan.name}
          subtitle={contactPlan.description || null}
          planId={contactPlan.id}
        />
      </div>
    </div>
  ) : null

  if (layout === 'table') {
    return (
      <div>
        <style>{pricingStyles}</style>
        {purchaseModal}
        {contactModal}
        {heading && <h2 class="hsw-pricing-title">{heading}</h2>}
        {filterBar}
        {visibleGroups.map((group, gi) => (
          <div key={group.category ?? `group-${gi}`} class="hsw-pricing-group">
            {group.category && (
              <h2 class="hsw-pricing-category">{categoryLabel(group.category)}</h2>
            )}
            <div class="hsw-pricing-table-wrap">
              <table class="hsw-pricing-table">
                <thead>
                  <tr>
                    <th>{str.plan}</th>
                    <th class="hsw-pricing-table-num">{str.price}</th>
                    {showFeatures && <th>{str.includes}</th>}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {grupperPlaner(group.plans).map(kort => { const plan = kortPlan(kort); return (
                    <tr key={kort.noegle} class={plan.isComingSoon ? 'hsw-pricing-table-soon' : ''}>
                      <td>
                        <span class="hsw-pricing-table-name">{kortTitel(kort)}</span>
                        {periodeVaelger(kort)}
                        {plan.isPopular && !plan.isComingSoon && (
                          <span class="hsw-pricing-table-tag">{str.popular}</span>
                        )}
                      </td>
                      <td class="hsw-pricing-table-num">
                        {showsPrice(plan) && (
                          <>
                            {plan.vatFreeAll && <span data-plan-price-from>{str.priceFromPrefix} </span>}
                            {formatMoney(plan.price, initData.locale, initData.currency)}
                            {plan.periodSuffix && (
                              <span class="hsw-pricing-table-period"> {plan.periodSuffix}</span>
                            )}
                          </>
                        )}
                      </td>
                      {showFeatures && (
                        <td class="hsw-pricing-table-features">
                          {(plan.features || []).slice(0, 3).join(', ')}
                        </td>
                      )}
                      <td class="hsw-pricing-table-cta">
                        {renderCta(plan, undefined, false)}
                      </td>
                    </tr>
                  ) })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (layout === 'compact') {
    return (
      <div>
        <style>{pricingStyles}</style>
        {purchaseModal}
        {contactModal}
        {heading && <h2 class="hsw-pricing-title">{heading}</h2>}
        {filterBar}
        {visibleGroups.map((group, gi) => (
          <div key={group.category ?? `group-${gi}`} class="hsw-pricing-group">
            {group.category && (
              <h2 class="hsw-pricing-category">{categoryLabel(group.category)}</h2>
            )}
            <div class="hsw-pricing-compact-list">
              {grupperPlaner(group.plans).map(kort => { const plan = kortPlan(kort); return (
                <div key={kort.noegle} class={`hsw-pricing-compact-row${plan.isComingSoon ? ' hsw-pricing-compact-soon' : ''}`}>
                  <span class="hsw-pricing-compact-name">{kortTitel(kort)}</span>
                  {periodeVaelger(kort)}
                  <span class="hsw-pricing-compact-price">
                    {showsPrice(plan) && (
                      <>
                        {plan.vatFreeAll && <span data-plan-price-from>{str.priceFromPrefix} </span>}
                        {formatMoney(plan.price, initData.locale, initData.currency)}
                        {plan.periodSuffix && (
                          <span class="hsw-pricing-compact-period"> {plan.periodSuffix}</span>
                        )}
                      </>
                    )}
                  </span>
                  {renderCta(plan, 'hsw-pricing-compact-cta', false)}
                </div>
              ) })}
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div>
      <style>{pricingStyles}</style>
      {purchaseModal}
      {contactModal}
      {heading && <h2 class="hsw-pricing-title">{heading}</h2>}
      {filterBar}
      {visibleGroups.map((group, gi) => (
        <div key={group.category ?? `group-${gi}`} class="hsw-pricing-group">
          {group.category && (
            <h2 class="hsw-pricing-category">{categoryLabel(group.category)}</h2>
          )}
          <div class="hsw-pricing-grid">
            {grupperPlaner(group.plans).map(renderCard)}
          </div>
        </div>
      ))}
    </div>
  )
}

const pricingStyles = `
  .hsw-period-picker { display: flex; flex-wrap: wrap; justify-content: center; gap: 0.4rem; margin: 0.5rem 0 1rem; }
  .hsw-period-option {
    font: inherit; font-size: 0.85rem; font-weight: 600; cursor: pointer;
    padding: 0.35rem 0.8rem; border-radius: 9999px;
    border: 1px solid #d4d4d4; background: transparent; color: inherit;
  }
  .hsw-period-option:hover { border-color: var(--yoga-primary, #FF6B35); }
  .hsw-period-option.hsw-period-active {
    background: var(--yoga-primary, #FF6B35); border-color: var(--yoga-primary, #FF6B35); color: #fff;
  }
  .hsw-pricing-table .hsw-period-picker,
  .hsw-pricing-compact-row .hsw-period-picker { justify-content: flex-start; margin: 0.35rem 0 0; }
  .hsw-price-vatfree { display: inline-block; margin-top: 0.35rem; font-size: 0.75rem; font-weight: 600; color: #16a34a; background: #f0fdf4; padding: 2px 8px; border-radius: 9999px; }
  .hsw-price-permonth { font-size: 0.85rem; color: #737373; margin-top: 0.25rem; }
  .hsw-pricing-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
    max-width: 1100px;
    margin: 0 auto;
  }
  .hsw-pricing-filter {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.5rem;
    margin: 0 auto 2rem auto;
  }
  .hsw-pricing-chip {
    font: inherit;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    color: inherit;
    background: transparent;
    border: 1px solid currentColor;
    border-radius: 999px;
    padding: 0.45rem 1.1rem;
    line-height: 1;
    opacity: 0.7;
  }
  .hsw-pricing-chip:hover {
    opacity: 1;
  }
  .hsw-pricing-chip.hsw-chip-active {
    opacity: 1;
    background: var(--yoga-primary, currentColor);
    border-color: transparent;
    color: #fff;
  }
  .hsw-pricing-title {
    font-size: 2rem;
    font-weight: 700;
    color: var(--yoga-text, #0a0a0a);
    text-align: center;
    margin: 0 auto 2rem;
    max-width: 1100px;
    letter-spacing: -0.01em;
  }
  .hsw-pricing-group + .hsw-pricing-group {
    margin-top: 3rem;
  }
  .hsw-pricing-category {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--yoga-text, #0a0a0a);
    text-align: center;
    margin: 0 auto 1.5rem;
    max-width: 1100px;
    letter-spacing: -0.01em;
  }
  .hsw-price-card {
    background: var(--yoga-card-bg, #ffffff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    padding: 2.5rem 2rem;
    text-align: center;
    position: relative;
    transition: all .3s;
    display: flex;
    flex-direction: column;
  }
  .hsw-price-card:hover {
    border-color: rgba(0,0,0,0.2);
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  }
  .hsw-price-card.hsw-popular {
    border: 2px solid var(--yoga-accent, var(--yoga-primary, #FF6B35));
    box-shadow: 0 8px 30px rgba(255,107,53,0.12);
  }
  .hsw-price-card.hsw-coming-soon {
    opacity: 0.65;
  }
  .hsw-badge-popular, .hsw-badge-soon {
    position: absolute;
    top: -14px;
    left: 50%;
    transform: translateX(-50%);
    background: linear-gradient(to right, var(--yoga-primary, #FF6B35), var(--yoga-secondary, var(--yoga-primary-900, #1A1A2E)));
    color: #fff;
    padding: 6px 24px;
    border-radius: 99px;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    box-shadow: 0 4px 14px rgba(255,107,53,0.3);
    white-space: nowrap;
  }
  .hsw-badge-soon { background: #737373; box-shadow: none; }
  .hsw-price-name {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 0.25rem;
    color: var(--yoga-text, #0a0a0a);
    letter-spacing: -0.01em;
  }
  .hsw-price-amount-row {
    margin: 1.5rem 0 0.25rem;
    line-height: 1;
  }
  .hsw-price-amount {
    font-size: 3.5rem;
    font-weight: 800;
    color: var(--yoga-text, #0a0a0a);
    line-height: 1;
    letter-spacing: -0.04em;
  }
  .hsw-price-suffix {
    font-size: 0.85rem;
    color: var(--yoga-text-muted, #737373);
    margin-left: 0.4rem;
    font-weight: 500;
  }
  .hsw-price-period {
    font-size: 0.85rem;
    color: var(--yoga-text-muted, #737373);
    margin: 0;
  }
  .hsw-price-desc {
    font-size: 0.95rem;
    color: var(--yoga-text-muted, #525252);
    margin: 0.75rem 0 0;
    line-height: 1.5;
  }
  .hsw-price-features {
    list-style: none;
    padding: 0;
    margin: 1.75rem 0 1.75rem;
    text-align: left;
  }
  .hsw-price-features li {
    padding: 0.55rem 0;
    padding-left: 1.75rem;
    position: relative;
    font-size: 0.9rem;
    color: var(--yoga-text, #404040);
    line-height: 1.4;
  }
  .hsw-check {
    position: absolute;
    left: 0;
    color: var(--yoga-primary, #FF6B35);
    font-weight: 700;
    font-size: 1rem;
  }
  .hsw-price-cta {
    display: block;
    margin-top: auto;
    padding: 0.9rem 1.25rem;
    text-align: center;
    background: transparent;
    color: var(--yoga-text, #0a0a0a);
    font-weight: 500;
    border: 1px solid #d4d4d4;
    border-radius: var(--yoga-radius, 0);
    text-decoration: none;
    font-size: 0.9rem;
    letter-spacing: 0.01em;
    transition: all .3s;
  }
  .hsw-price-cta:hover {
    background: var(--yoga-text, #0a0a0a);
    color: var(--yoga-card-bg, #fff);
    border-color: var(--yoga-text, #0a0a0a);
  }
  .hsw-price-card.hsw-popular .hsw-price-cta {
    background: var(--yoga-primary, #FF6B35);
    color: #fff;
    border-color: var(--yoga-primary, #FF6B35);
  }
  .hsw-price-card.hsw-popular .hsw-price-cta:hover {
    background: var(--yoga-primary-900, #1A1A2E);
    border-color: var(--yoga-primary-900, #1A1A2E);
  }
  .hsw-price-cta[aria-disabled="true"] {
    cursor: not-allowed;
    opacity: 0.6;
  }

  /* ===== «Kontakt mig»-vindue ===== */
  .hsw-cp-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100000;
    padding: 1rem;
  }
  .hsw-cp-dialog {
    position: relative;
    width: 100%;
    max-width: 640px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0,0,0,0.2);
    box-sizing: border-box;
  }
  .hsw-cp-close {
    position: absolute;
    top: 0.5rem;
    right: 0.75rem;
    z-index: 1;
    background: transparent;
    border: 0;
    font-size: 1.75rem;
    line-height: 1;
    color: var(--yoga-text-muted, #737373);
    cursor: pointer;
    padding: 0.25rem 0.6rem;
  }
  .hsw-cp-close:hover { color: var(--yoga-text, #0a0a0a); }

  /* ===== Table layout ===== */
  .hsw-pricing-table-wrap {
    max-width: 900px;
    margin: 0 auto;
    overflow-x: auto;
  }
  .hsw-pricing-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.95rem;
    color: var(--yoga-text, #0a0a0a);
  }
  .hsw-pricing-table th,
  .hsw-pricing-table td {
    padding: 0.85rem 1rem;
    text-align: left;
    border-bottom: 1px solid var(--yoga-card-border, #e5e7eb);
  }
  .hsw-pricing-table th {
    font-weight: 600;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--yoga-text-muted, #525252);
  }
  .hsw-pricing-table-num {
    text-align: right !important;
    white-space: nowrap;
    font-weight: 600;
  }
  .hsw-pricing-table-period {
    font-weight: 400;
    color: var(--yoga-text-muted, #737373);
    font-size: 0.8rem;
  }
  .hsw-pricing-table-name {
    font-weight: 600;
  }
  .hsw-pricing-table-tag {
    margin-left: 0.5rem;
    display: inline-block;
    padding: 2px 8px;
    border-radius: 99px;
    background: rgba(255,107,53,0.12);
    color: var(--yoga-accent, var(--yoga-primary, #FF6B35));
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }
  .hsw-pricing-table-features {
    color: var(--yoga-text-muted, #525252);
    font-size: 0.85rem;
  }
  .hsw-pricing-table-cta {
    text-align: right;
  }
  .hsw-pricing-table-cta a {
    display: inline-block;
    padding: 0.5rem 1rem;
    background: var(--yoga-primary, #FF6B35);
    color: #fff;
    border-radius: var(--yoga-radius, 0);
    text-decoration: none;
    font-weight: 600;
    font-size: 0.85rem;
    transition: background .2s;
  }
  .hsw-pricing-table-cta a:hover { background: var(--yoga-primary-900, #1A1A2E); }
  .hsw-pricing-table-cta a[aria-disabled="true"] { opacity: 0.5; pointer-events: none; }
  .hsw-pricing-table-soon { opacity: 0.6; }

  /* ===== Compact layout ===== */
  .hsw-pricing-compact-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-width: 640px;
    margin: 0 auto;
  }
  .hsw-pricing-compact-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.85rem 1rem;
    background: var(--yoga-card-bg, #ffffff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
  }
  .hsw-pricing-compact-row:hover { border-color: rgba(0,0,0,0.2); }
  .hsw-pricing-compact-name {
    flex: 1;
    font-weight: 600;
    color: var(--yoga-text, #0a0a0a);
    font-size: 0.95rem;
  }
  .hsw-pricing-compact-price {
    font-weight: 600;
    color: var(--yoga-text, #0a0a0a);
    white-space: nowrap;
  }
  .hsw-pricing-compact-period {
    font-weight: 400;
    color: var(--yoga-text-muted, #737373);
    font-size: 0.8rem;
  }
  .hsw-pricing-compact-cta {
    padding: 0.5rem 1rem;
    background: var(--yoga-primary, #FF6B35);
    color: #fff;
    border-radius: var(--yoga-radius, 0);
    text-decoration: none;
    font-weight: 600;
    font-size: 0.85rem;
    white-space: nowrap;
    transition: background .2s;
  }
  .hsw-pricing-compact-cta:hover { background: var(--yoga-primary-900, #1A1A2E); }
  .hsw-pricing-compact-cta[aria-disabled="true"] { opacity: 0.5; pointer-events: none; }
  .hsw-pricing-compact-soon { opacity: 0.6; }
  @media (max-width: 540px) {
    .hsw-pricing-compact-row {
      flex-wrap: wrap;
      gap: 0.5rem;
    }
    .hsw-pricing-compact-name { flex: 1 1 100%; }
  }
`

const skeletonStyles = `
  .hsw-skeleton-card {
    background: var(--yoga-card-bg, #ffffff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    padding: 2.5rem 2rem;
  }
  .hsw-skeleton {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: hsw-shimmer 1.5s infinite;
    border-radius: 4px;
    margin-bottom: 0.5rem;
  }
  .hsw-sk-name { height: 24px; width: 60%; margin: 0 auto 1.5rem; }
  .hsw-sk-price { height: 48px; width: 50%; margin: 0 auto 0.5rem; }
  .hsw-sk-period { height: 16px; width: 40%; margin: 0 auto 1.5rem; }
  .hsw-sk-line { height: 14px; width: 100%; margin-bottom: 0.6rem; }
  .hsw-sk-button { height: 44px; width: 100%; margin-top: 1.5rem; }
  @keyframes hsw-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`
